import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
} from "@mui/icons-material";

import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AppTable } from "../../../../components/AppTable";
import { TableColumn } from "../../../../app.interface";
import {
  businessService,
  AuditLog,
  AuditLogRequest,
} from "../../../../services/business.service";
import {
  AuditLogDetailsDialog,
  AuditLogTableData,
} from "./AuditLogDetailsDialog";
import { startCase } from "lodash";
import { getModuleColor } from "./constants";

export const AuditLogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [auditLogs, setAuditLogs] = useState<AuditLogTableData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1); // AppTable uses 1-based pagination
  const [pageSize, setPageSize] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  // Filters state
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedModule, setSelectedModule] = useState<string>("");
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<string>("");

  // Sorting state
  const [sortField, setSortField] = useState("timestamp");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Details dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] =
    useState<AuditLogTableData | null>(null);

  // Define table columns for AppTable
  const columns: TableColumn[] = [
    {
      id: "user_email",
      header: "User",
      field: "user_email",
      type: "action",
      width: "200px",
      sortable: true,
      renderAction: (row) => {
        return (
          <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
            {row?.user_email || "-"}
          </Typography>
        );
      },
    },
    {
      id: "timestamp",
      header: "Date & Time",
      field: "timestamp",
      type: "datetime",
      width: "180px",
      sortable: true,
      dateFormat: "absolute",
    },

    {
      id: "module",
      header: "Module",
      field: "module",
      type: "action",
      width: "120px",
      sortable: true,
      renderAction: (row: any) => {
        return (
          <Chip
            label={startCase(row?.module) || "-"}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: "2px",
              fontSize: "12px",
              fontWeight: "bold",
              borderColor: getModuleColor(row?.module),
              backgroundColor: getModuleColor(row?.module),
              color: "white !important",
            }}
          />
        );
      },
    },
    {
      id: "action",
      header: "Action",
      field: "action",
      type: "text",
      width: "150px",
      sortable: true,
      renderAction: (row: any) => {
        return (
          <Chip
            label={row?.action || "N/A"}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: "2px",
              fontSize: "12px",
              fontWeight: "bold",
            }}
            icon={<ViewIcon />}
          />
        );
      },
    },
    {
      id: "document_type",
      header: "Document Type",
      field: "document_type",
      type: "text",
      width: "140px",
      sortable: true,
      accessor: (row: Record<string, any>) => row.document_type || "-",
    },
    {
      id: "is_system_generated",
      header: "Source",
      field: "is_system_generated",
      type: "status",
      width: "100px",
      sortable: true,
      accessor: (row: Record<string, any>) =>
        row.is_system_generated ? "system" : "user",
      statusConfig: {
        system: { label: "System", color: "gray" },
        user: { label: "User", color: "blue" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      field: "actions",
      type: "action",
      width: "120px",
      renderAction: (row: any) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleViewDetails(row as AuditLogTableData);
          }}
          sx={{ minWidth: 80 }}
        >
          Details
        </Button>
      ),
    },
  ];

  const fetchAuditLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const requestData: AuditLogRequest = {
        pageIndex: page - 1, // Convert to 0-based for API
        pageSize: pageSize,
        sortField,
        sortOrder,
        ...(searchQuery && { searchQuery }),
        ...(startDate && { startDate: Math.floor(startDate.getTime() / 1000) }),
        ...(endDate && { endDate: Math.floor(endDate.getTime() / 1000) }),
        ...(selectedModule && { module: selectedModule }),
        ...(selectedAction && { action: selectedAction }),
        ...(selectedUser && { user_email: selectedUser }),
      };

      const response = await businessService.getAuditLogs(requestData);

      const transformedData: AuditLogTableData[] = response.data.audit_logs.map(
        (log: any) => ({
          ...log,
          _id: log.log_id, // AppTable expects _id field
        })
      );

      setAuditLogs(transformedData);
      setTotalItems(response.data.pagination.total_count.value);
    } catch (err: any) {
      setError(err.message);
      setAuditLogs([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    sortField,
    sortOrder,
    searchQuery,
    startDate,
    endDate,
    selectedModule,
    selectedAction,
    selectedUser,
  ]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page
  };

  const handleSort = (field: string, order: "asc" | "desc") => {
    setSortField(field);
    setSortOrder(order);
    setPage(1); // Reset to first page
  };

  const handleSearch = () => {
    setPage(1); // Reset to first page
    fetchAuditLogs();
  };

  const handleReset = () => {
    setSearchQuery("");
    setStartDate(null);
    setEndDate(null);
    setSelectedModule("");
    setSelectedAction("");
    setSelectedUser("");
    setPage(1);
  };

  const handleViewDetails = (row: any) => {
    setSelectedAuditLog(row as AuditLogTableData);
    setDetailsDialogOpen(true);
  };

  const handleRowClick = (row: any) => {
    handleViewDetails(row as AuditLogTableData);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Clean Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 4, py: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={3}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/app-launcher")}
                sx={{
                  borderColor: "primary.main",
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.main",
                    color: "white",
                  },
                }}
              >
                Back
              </Button>

              <Stack direction="row" alignItems="center" spacing={2}>
                <HistoryIcon
                  sx={{
                    color: "primary.main",
                    fontSize: 28,
                  }}
                />
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: { xs: "1.75rem", md: "2.125rem" },
                    }}
                  >
                    Audit Logs
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.875rem",
                    }}
                  >
                    System activity tracking
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchAuditLogs}
              disabled={loading}
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": {
                  bgcolor: "primary.main",
                  color: "white",
                },
                "&:disabled": {
                  borderColor: "grey.300",
                  color: "grey.400",
                },
              }}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          </Stack>
        </motion.div>

        {/* Filters */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
        >
          <Paper
            elevation={0}
            variant="outlined"
            sx={{ p: 3, mb: 3, borderRadius: 2 }}
          >
            <Stack spacing={3}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <FilterIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Filters
                </Typography>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by user, action, or document type..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />

                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate ? startDate.toISOString().split("T")[0] : ""}
                  onChange={(e) =>
                    setStartDate(
                      e.target.value ? new Date(e.target.value) : null
                    )
                  }
                  sx={{ minWidth: 160 }}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="End Date"
                  type="date"
                  value={endDate ? endDate.toISOString().split("T")[0] : ""}
                  onChange={(e) =>
                    setEndDate(e.target.value ? new Date(e.target.value) : null)
                  }
                  sx={{ minWidth: 160 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel>Module</InputLabel>
                  <Select
                    value={selectedModule}
                    onChange={(e) => setSelectedModule(e.target.value)}
                    label="Module"
                  >
                    <MenuItem value="">All Modules</MenuItem>
                    <MenuItem value="business-admin">Business Admin</MenuItem>
                    <MenuItem value="sessions">Sessions</MenuItem>
                    <MenuItem value="invoices">Invoices</MenuItem>
                    <MenuItem value="pos">POS</MenuItem>
                    <MenuItem value="inventory">Inventory</MenuItem>
                    <MenuItem value="accounting">Accounting</MenuItem>
                    <MenuItem value="hr">HR</MenuItem>
                    <MenuItem value="orders">Orders</MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={selectedAction}
                    onChange={(e) => setSelectedAction(e.target.value)}
                    label="Action"
                  >
                    <MenuItem value="">All Actions</MenuItem>
                    <MenuItem value="list_audit_logs">List Audit Logs</MenuItem>
                    <MenuItem value="start_session">Start Session</MenuItem>
                    <MenuItem value="list_invoices">List Invoices</MenuItem>
                    <MenuItem value="get_invoice">Get Invoice</MenuItem>
                    <MenuItem value="update_invoice">Update Invoice</MenuItem>
                    <MenuItem value="get_approved_grns">
                      Get Approved GRNs
                    </MenuItem>
                    <MenuItem value="create">Create</MenuItem>
                    <MenuItem value="update">Update</MenuItem>
                    <MenuItem value="delete">Delete</MenuItem>
                    <MenuItem value="view">View</MenuItem>
                    <MenuItem value="login">Login</MenuItem>
                    <MenuItem value="logout">Logout</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="User Email"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  placeholder="Filter by user email..."
                  sx={{ minWidth: 200 }}
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                  sx={{ minWidth: 120 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ minWidth: 120 }}
                >
                  Reset
                </Button>
                <Button
                  variant="outlined"
                  onClick={fetchAuditLogs}
                  startIcon={
                    loading ? <CircularProgress size={16} /> : <RefreshIcon />
                  }
                  disabled={loading}
                  sx={{ minWidth: 120 }}
                >
                  Refresh
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </motion.div> */}

        {/* Error Alert */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </motion.div>
        )}

        {/* App Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
        >
          <AppTable
            data={auditLogs}
            columns={columns}
            loading={loading}
            onRowClick={handleRowClick}
            sortConfig={{ field: sortField, direction: sortOrder }}
            onSort={handleSort}
            pagination={{
              total: totalItems,
              page: page,
              pageSize: pageSize,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            styleOptions={{
              bordered: true,
              columnSeparators: false,
            }}
            emptyState={{
              icon: "history",
              title: "No Audit Logs Found",
              description:
                "There are no audit logs matching your current filters.",
            }}
          />
        </motion.div>

        {/* Details Dialog */}
        <AuditLogDetailsDialog
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          auditLog={selectedAuditLog}
        />
      </Container>
    </motion.div>
  );
};
